import { Select as MuiSelect, SelectProps as MuiSelectProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ChevronUp, ChevronDown } from '@bolteu/kalep-react-icons';

import { tokens } from '../../theme/styles/themeMui';
import { MENU_LIST_STYLES, MENU_PAPER_STYLES } from '../Menu/menuStyles';

// Now we can use MuiSelectProps directly since we extended the theme
export type SelectProps = MuiSelectProps;

// Custom dropdown icon component
const SelectIcon = ({ open, ...props }: { open?: boolean; [key: string]: unknown }) => {
  return open ? <ChevronUp {...props} /> : <ChevronDown {...props} />;
};

const StyledSelect = styled(
  MuiSelect,
  {},
)<SelectProps>(({ theme, size = 'medium' }) => ({
  '&.MuiSelect-root': {
    ...tokens.typography.bodyMRegular,
    borderColor: theme.palette.colors.border['neutral-secondary'],
    backgroundColor: theme.palette.colors.core.white,
    borderWidth: '2px',
    borderRadius: '8px',
    '& input': {
      padding: '8px 16px',
    },
    '& fieldset': {
      borderColor: theme.palette.colors.border['neutral-secondary'],
      borderWidth: '1px',
    },
    '&:hover fieldset': {
      borderColor: theme.palette.colors.border['neutral-secondary'],
      borderWidth: '1px',
    },
    '&.Mui-focused': {
      backgroundColor: theme.palette.colors.special.nulled,
      '& fieldset': {
        borderColor: theme.palette.colors.border['neutral-secondary'],
        borderWidth: '1px',
      },
    },
    '.MuiInputBase-input.MuiSelect-select.MuiSelect-select': {
      ...(size === 'small' && {
        paddingTop: '8px',
        paddingBottom: '8px',
        paddingLeft: '14px',
        paddingRight: '40px',
        ...tokens.typography['body-s-regular'],
      }),
      ...(size === 'medium' && {
        paddingTop: '12px',
        paddingBottom: '12px',
        paddingLeft: '12px',
        paddingRight: '44px',
        ...tokens.typography['body-m-regular'],
      }),
      ...(size === 'large' && {
        paddingTop: '16px',
        paddingBottom: '16px',
        paddingLeft: '16px',
        paddingRight: '48px',
        ...tokens.typography['body-m-regular'],
      }),
    },
  },
  [`& .MuiSelect-icon`]: {
    color: theme.palette.colors.content.primary,
    top: 'unset',
    ...(size === 'small' && {
      height: '18px',
      width: '18px',
      right: '14px',
    }),
    ...(size === 'medium' && {
      height: '24px',
      width: '24px',
      right: '12px',
    }),
    ...(size === 'large' && {
      height: '24px',
      width: '24px',
      right: '16px',
    }),
  },
}));

// Wrapper component that uses custom icon
export function Select({ MenuProps, ...props }: SelectProps) {
  return (
    <StyledSelect
      {...props}
      IconComponent={SelectIcon}
      MenuProps={{
        ...MenuProps,
        slotProps: {
          ...MenuProps?.slotProps,
          paper: {
            ...MenuProps?.slotProps?.paper,
            sx: {
              ...MENU_PAPER_STYLES,
              marginTop: '4px',
              ...((MenuProps?.slotProps?.paper as Record<string, unknown>)?.sx as Record<
                string,
                unknown
              >),
            },
          },
        },
        sx: (() => {
          const { '& .MuiList-root': consumerListStyles, ...restSx } =
            (MenuProps?.sx as Record<string, unknown>) || {};
          return {
            '& .MuiList-root': {
              ...MENU_LIST_STYLES,
              ...(consumerListStyles as Record<string, unknown>),
            },
            ...restSx,
          };
        })(),
      }}
    />
  );
}
